/**
 * Centralized utility for AI Similarity feature availability logic
 * Used for AI Search, AI Content Recommendation, and other similarity-based AI features
 * that follow the pattern: feature flag + config enabled + entities configured
 */

import { isFeatureEnabled } from 'hb-react/shared/utils/features';
import { type NetworkConfiguration } from 'hb-react/types/network';

interface AISimilarityEntitiesConfig {
  config?: {
    enabled_entities?: Array<string>;
  };
}

export interface FeatureFlags {
  flagUjAiContentRecommendation?: boolean | null;
  flagUjAiContentRecommendationForumEvents?: boolean | null;
  flagUjAiGlobalSearch?: boolean | null;
  flagUjAiSelectContentType?: boolean | null;
}

/**
 * Generic function to check if an AI Similarity feature is enabled in network configuration
 * Automatically determines which feature flag to check based on the feature name
 * @param featureName - Feature name (e.g., 'ai_global_search', 'ai_content_recommendation')
 * @param networkConfiguration - Network configuration object
 * @param flags - Feature flags object containing all flags
 * @returns true if feature flag is enabled AND config.enabled is true
 */
export const isAISimilarityFeatureEnabled = (
  featureName: string,
  networkConfiguration:
    | NetworkConfiguration
    | Record<string, { enabled?: boolean }>
    | null
    | undefined,
  flags: FeatureFlags | null | undefined,
): boolean => {
  // Check if the feature flag is enabled
  if (!flags) {
    return false;
  }

  const flagEnabled =
    featureName === 'ai_global_search'
      ? !!flags.flagUjAiGlobalSearch
      : !!(
          flags.flagUjAiContentRecommendation ||
          flags.flagUjAiContentRecommendationForumEvents
        );

  if (!flagEnabled) {
    return false;
  }

  // Use the generic isFeatureEnabled for consistency
  // Convert null to undefined as isFeatureEnabled doesn't accept null
  return isFeatureEnabled(networkConfiguration || undefined, featureName);
};

/**
 * Generic function to check if an AI Similarity feature has entities configured
 * Works for ai_global_search, ai_content_recommendation, and other similarity features
 * @param entitiesConfig - AI similarity feature configuration with enabled_entities
 * @param flags - Feature flags object (optional)
 * @returns true if AI similarity feature has enabled entities
 */
export const hasAISimilarityEntitiesEnabled = (
  entitiesConfig: AISimilarityEntitiesConfig | null | undefined,
  flags?: FeatureFlags | null | undefined,
): boolean => {
  if (!entitiesConfig) {
    return false;
  }

  // When flag is OFF, all entity types are enabled (ignoring stored config)
  if (flags && !flags.flagUjAiSelectContentType) {
    return true;
  }

  // When flag is ON, check stored enabled_entities from config
  const enabledEntities = entitiesConfig.config?.enabled_entities || [];
  return enabledEntities.length > 0;
};

/**
 * Determines which search type should be used (AI or Legacy)
 * @param networkConfiguration - Network configuration object
 * @param flags - Feature flags object
 * @returns true if AI search should be used, false for legacy
 */
export const shouldUseAISearch = (
  networkConfiguration:
    | Record<string, { enabled?: boolean }>
    | null
    | undefined,
  flags: FeatureFlags | null | undefined,
): boolean =>
  isAISimilarityFeatureEnabled('ai_global_search', networkConfiguration, flags);

/**
 * Checks if legacy search is available
 * @param searchDeactivateFlagEnabled - flagUeSearchDeactivateGlobal value
 * @returns true if legacy search is available
 */
export const isLegacySearchAvailable = (
  searchDeactivateFlagEnabled: boolean | null | undefined,
): boolean => !searchDeactivateFlagEnabled;

/**
 * Checks if manual related events recommendation flags are enabled
 * Requires both feature flags to be enabled (flags only, no config check)
 * Mirrors Ruby: Features::AI::Similarity.show_related_events_in_recommendation_enabled?
 *
 * @param flags - Feature flags object
 * @returns true if both flagUjAiContentRecommendation and flagUjAiContentRecommendationForumEvents are enabled
 */
export const isManualRelatedEventsInContentRecommendationEnabled = (
  flags: FeatureFlags | null | undefined,
): boolean => {
  if (!flags) {
    return false;
  }

  return !!(
    flags.flagUjAiContentRecommendation &&
    flags.flagUjAiContentRecommendationForumEvents
  );
};

/**
 * Checks if manual related events feature is fully enabled (flags + config)
 * Checks both feature flags AND network configuration for ai_content_recommendation
 * Mirrors Ruby: Features::AI::Similarity.content_recommendation_event_enabled?
 *
 * @param networkConfiguration - Network configuration object
 * @param flags - Feature flags object
 * @returns true if flags are enabled AND ai_content_recommendation config is enabled
 */
export const isManualRelatedAndContentRecommendationBothEnabled = (
  networkConfiguration: NetworkConfiguration | null | undefined,
  flags: FeatureFlags | null | undefined,
): boolean => {
  if (!isManualRelatedEventsInContentRecommendationEnabled(flags)) {
    return false;
  }

  return isFeatureEnabled(
    networkConfiguration || undefined,
    'ai_content_recommendation',
  );
};
